import React, { useState, useEffect } from 'react';
import { competitorAPI, aiV2API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Eye, Plus, Trash2, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCompetitors() {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', rss_url: '' });
  const [viewComp, setViewComp] = useState(null);
  const [articles, setArticles] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [analysing, setAnalysing] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const r = await competitorAPI.list(); setCompetitors(r.data.competitors || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const addCompetitor = async () => {
    try { await competitorAPI.add(form); toast.success('Competitor added'); setShowAdd(false); setForm({ name: '', url: '', rss_url: '' }); load(); }
    catch { toast.error('Failed to add'); }
  };

  const fetchFeed = async (id) => {
    setFetching(true);
    try {
      const r = await competitorAPI.fetch(id);
      toast.success(`Fetched ${r.data.fetched} articles`);
      setArticles(r.data.articles || []);
    } catch { toast.error('Failed to fetch feed'); }
    finally { setFetching(false); }
  };

  const viewArticles = async (comp) => {
    setViewComp(comp);
    setAnalysis(null);
    try {
      const r = await competitorAPI.getArticles(comp.id);
      setArticles(r.data.articles || []);
    } catch { setArticles([]); }
  };

  const analyseArticle = async (article) => {
    setAnalysing(article.title);
    try {
      const r = await aiV2API.contentGap({ categories: [], recent_articles: [{ title: article.title }] });
      setAnalysis({ title: article.title, ...r.data });
      toast.success('Analysis complete');
    } catch { toast.error('AI analysis failed'); }
    finally { setAnalysing(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Eye className="w-6 h-6 text-[#B89B5E]" /> Competitor Monitoring</h1>
          <p className="text-sm text-[#5B616E] mt-1">Track competitor content and identify editorial opportunities</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> Add Competitor</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? [1,2,3].map(i => <Skeleton key={i} className="h-36" />) : competitors.map(c => (
          <Card key={c.id} className="bg-white border-[#E7E0D6]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-sm">{c.name}</h3>
                  <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-[#B89B5E] hover:underline">{c.url}</a>
                </div>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={async () => { await competitorAPI.remove(c.id); load(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
              {c.rss_url && <p className="text-[10px] text-[#5B616E] mb-3">RSS: {c.rss_url}</p>}
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => { viewArticles(c); fetchFeed(c.id); }} disabled={fetching}>
                  <RefreshCw className={`w-3 h-3 mr-1 ${fetching ? 'animate-spin' : ''}`} /> Fetch
                </Button>
                <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => viewArticles(c)}>
                  <Eye className="w-3 h-3 mr-1" /> View
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {viewComp && (
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader>
            <CardTitle className="text-sm">Latest articles from {viewComp.name}</CardTitle>
          </CardHeader>
          <CardContent>
            {articles.length === 0 ? (
              <p className="text-sm text-[#5B616E]">No articles fetched. Click "Fetch" to load the RSS feed.</p>
            ) : (
              <div className="space-y-3">
                {articles.map((a, i) => (
                  <div key={i} className="flex items-start justify-between p-3 bg-[#FBF7EF] rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <a href={a.link} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline text-[#121316] block">{a.title}</a>
                      <p className="text-xs text-[#5B616E] mt-0.5">{a.pub_date}</p>
                    </div>
                    <Button size="sm" variant="outline" className="text-xs flex-shrink-0" onClick={() => analyseArticle(a)} disabled={analysing === a.title}>
                      <Sparkles className="w-3 h-3 mr-1" />
                      {analysing === a.title ? 'Analysing…' : 'AI Analyse'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {analysis && (
              <div className="mt-4 p-4 border border-[#B89B5E]/30 bg-[#B89B5E]/5 rounded-xl">
                <p className="text-sm font-semibold mb-2">AI Analysis: "{analysis.title}"</p>
                {(analysis.gaps || []).map((g, i) => (
                  <div key={i} className="text-xs mb-1"><Badge variant="outline" className="text-[10px] mr-1">{g.priority}</Badge>{g.topic} — {g.reason}</div>
                ))}
                {(analysis.recommended_next || []).map((r, i) => (
                  <p key={i} className="text-xs text-green-700 mt-1">💡 {r}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Competitor</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Competitor name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Input placeholder="Website URL" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
            <Input placeholder="RSS Feed URL" value={form.rss_url} onChange={e => setForm(f => ({ ...f, rss_url: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={addCompetitor} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
